import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import NumberInputTextValue from 'assets/components/inputs/number/NumberInputTextValue';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { isValidValue } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { keys, values } from 'lodash';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import payeeSaveApi from 'models/claims/payee/save/Api.Payee.Save';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import { CountryCode, CountryCodeFallback } from 'models/enums/CountryCode';
import LineItem from 'models/enums/LineItem';
import { CSSProperties, useEffect, useState } from 'react';
import { useClaimEditJobPayeeItem, useClaimJobEdit } from '../ClaimEdit.Job.Hooks';
import ClaimEditJobItemNewPayee from './components/ClaimEdit.Job.Item.NewPayee';

type Props = {
  lossCodes: Model.ILossCode[];
  index: number;
  ordinalNumber: number;
  data?: Utils.FormData<Model.IClaimJobItem> & { isWarrantyProduct: boolean };
  insurerHeaders?: string[];
  onDelete(data: Utils.FormData<Model.IClaimJobItem>, index: number): void;
  onUpdate(data: Partial<Model.IClaimJobItem>): void;
  countryCode?: Model.Enum.CountryCode;
};

export const addJobItemRowButtonStyles: CSSProperties = {
  backgroundColor: 'rgb(240, 139, 7)',
  color: Theme.Color.textAlt,
  marginLeft: 'auto',
};

export default function ClaimEditJobItem({
  data,
  index,
  ordinalNumber,
  lossCodes,
  insurerHeaders,
  onDelete,
  onUpdate,
  countryCode,
}: Props) {
  const { lang, translate } = getLocales();
  const {
    isEditingTax,
    setIsEditingTax,
    getJobItemDefaultValues,
    isClaimJobItemEditable,
    setNewPayeeId,
    newPayeeId,
    permissions,
    isClaimAdmin,
    isClaimOwner,
    claimJob,
    overrideLabour,
    canEditAdjustedAmt,
    canEditInsurerAdjudications,
  } = useClaimJobEdit();
  const { setOffset, setSearch, limit, offset, payeApi, paginatedPayees, searchedPayees } = useClaimEditJobPayeeItem();
  const [isCreatingPayee, setIsCreatingPayee] = useState<boolean>(false);

  const payee = new ClaimJobItemPayee(data?.claimJobItemPayee);

  const payeeId =
    data?.claimJobItemPayee?.payee?.id ?? data?.claimJobItemPayee?.client?.id ?? data?.claimJobItemPayee?.dealer?.id;
  const selectInputValue = payeeId ? `${payeeId}-${data?.claimJobItemPayee?.payeeType?.id}` : undefined;

  const canEditJobItem = !(
    isClaimAdmin ||
    (isClaimOwner && (data?.id ? permissions?.EDIT_CLAIMJOBITEM : permissions?.CREATE_CLAIMJOBITEM))
  );
  const isEditable = isClaimJobItemEditable || canEditJobItem;
  const canAddNewPayee =
    (permissions.CLAIM_ADMIN || (isClaimOwner && (permissions.CREATE_PAYEE || permissions.EDIT_PAYEE))) &&
    isCreatingPayee &&
    !newPayeeId;
  const displayedInsurerHeaders = insurerHeaders ?? keys(data?.insurerAdjudications);

  useEffect(() => {
    if (newPayeeId) payeApi.execute((b) => b);
  }, [newPayeeId]);

  return (
    <tr>
      <td style={{ paddingLeft: '25px', width: '20px' }}>{ordinalNumber + 1}</td>
      <td style={{ width: '200px' }}>
        {payee &&
        data?.id &&
        (claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_WAITING_ON_INVOICE || isEditable) ? (
          <span title={payee.displayInfo?.title}>{payee.displayInfo?.title}</span>
        ) : (
          <SelectInput
            name="claimJobItemPayee"
            data={paginatedPayees?.map((it) => ({ ...it.displayInfo, id: `${it.displayInfo.id}-${it.payeeType?.id}` }))}
            displayText={payee?.displayInfo?.title}
            value={selectInputValue}
            limit={limit}
            offset={offset}
            count={searchedPayees?.length}
            onAdd={() => {
              setNewPayeeId(null);
              setIsCreatingPayee(true);
            }}
            animateOut={() => {
              setOffset(0);
            }}
            onSearch={setSearch}
            onOffsetChange={({ value: offset }) => setOffset(offset)}
            disabled={isEditable}
            onChange={({ claimJobItemPayee: id }) => {
              const itemPayee = paginatedPayees?.find(
                (it) => `${it.payee?.id ?? it.dealer?.id ?? it.client?.id}-${it.payeeType?.id}` === id
              );
              onUpdate({
                claimJobItemPayee: itemPayee,
                ...getJobItemDefaultValues({ jobItemTypeCode: data?.jobItemTypeCode, claimJobItemPayee: itemPayee }),
                hoursWorked: data.jobItemTypeCode === LineItem.labour && !data.hoursWorked ? 1 : data.hoursWorked,
              });
              setOffset(0); // Reset offset
              setSearch(undefined); // Reset search
            }}
            htmlElementProps={{ title: payee?.displayInfo?.title }}
            style={{
              input: {
                fontSize: Theme.FontSize.S,
              },
            }}
          />
        )}
      </td>
      <td title={lossCodes?.find((it) => it.code === data?.lossCode)?.descnEn} style={{ width: '200px' }}>
        <SelectInput
          name="lossCode"
          data={lossCodes.map((it) => ({
            id: it.code,
            title: it.descnEn,
          }))}
          disabled={isEditable}
          value={data?.lossCode}
          onChange={({ lossCode }) => {
            onUpdate({
              lossCode,
              quantity: data.jobItemTypeCode === LineItem.part && !data.quantity ? 1 : data.quantity,
            });
          }}
          style={{
            input: {
              fontSize: Theme.FontSize.S,
            },
          }}
          htmlElementProps={{ title: lossCodes?.find((it) => it.code === data?.lossCode)?.descnEn }}
        />
      </td>
      {data?.jobItemTypeCode === LineItem.part && (
        <>
          <td style={{ width: '125px' }}>
            <TextInput
              name="partNumber"
              disabled={isEditable}
              value={data?.partNumber}
              onChange={onUpdate}
              maxLength={20}
              style={{ input: { textAlign: 'right' } }}
            />
          </td>
          <td style={{ width: '125px' }}>
            <NumberInput
              name="baseCost"
              value={Number(data?.baseCost?.toFixed(2))}
              onChange={onUpdate}
              readOnly={isEditable}
              style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
              iconBefore={<Icon class="fas-dollar-sign" />}
            />
          </td>
          <td style={{ width: '75px' }}>
            <NumberInput
              name="quantity"
              value={data?.quantity}
              onChange={onUpdate}
              disabled={isEditable}
              error={data?.quantity < 0 && lang.mustBeMoreThanAmount.replace('{amount}', '0')}
              style={{ input: { textAlign: 'center' } }}
            />
          </td>
        </>
      )}
      <td style={{ width: '75px', textAlign: 'center' }}>{translate(data?.statusCode)}</td>
      {data?.jobItemTypeCode === LineItem.labour && (
        <>
          <td style={{ width: '75px' }}>
            <NumberInput
              name="hoursWorked"
              value={Number(data?.hoursWorked?.toFixed(2))}
              onChange={onUpdate}
              disabled={isEditable}
              error={
                (data?.hoursWorked === null || data?.hoursWorked < 0) &&
                lang.mustBeMoreThanAmount.replace('{amount}', '0')
              }
              step={0.01}
              style={{ input: { textAlign: 'center' } }}
            />
          </td>
          <td style={{ width: '125px' }}>
            <NumberInput
              name="labourRate"
              value={Number(data?.labourRate?.toFixed(2))}
              onChange={onUpdate}
              disabled={!overrideLabour}
              style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
              iconBefore={<Icon class="fas-dollar-sign" />}
            />
          </td>
        </>
      )}
      <td style={{ width: '125px' }}>
        <NumberInput
          name="correction"
          value={Number(data?.correction?.toFixed(2))}
          onChange={onUpdate}
          step={0.01}
          readOnly={
            isEditable ||
            (data.jobItemTypeCode === LineItem.deductible && data?.isWarrantyProduct) ||
            data.jobItemTypeCode === LineItem.labour ||
            data.jobItemTypeCode === LineItem.part
          }
          style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      <td style={{ width: '125px' }}>
        <NumberInput
          name="adjudication"
          value={Number(data?.adjudication?.toFixed(2))}
          onChange={onUpdate}
          step={0.01}
          disabled={isEditable || !canEditAdjustedAmt}
          style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      {displayedInsurerHeaders.map((insurerKey) => {
        const insurerAdjudications = data?.insurerAdjudications ?? {};
        const value = insurerAdjudications[insurerKey];
        const insurerTotal = values(insurerAdjudications).reduce((sum, v) => sum + (v?.amount ?? 0), 0);
        const isInsurerAmountMissing = !isValidValue(value?.amount);
        const isInsurerTotalMismatched = Math.abs(insurerTotal - (data?.adjudication ?? 0)) > 0.009;
        return (
          <td key={insurerKey} style={{ width: '125px' }}>
            <NumberInput
              name="insurerAmount"
              value={value?.amount ?? undefined}
              step={0.01}
              error={
                canEditInsurerAdjudications &&
                (isInsurerAmountMissing
                  ? lang.mustNotBeEmpty
                  : isInsurerTotalMismatched &&
                    lang.insurerAdjudicationTotalMustMatchAdjustedAmt.replace(
                      '{amount}',
                      (data?.adjudication ?? 0).toFixed(2)
                    ))
              }
              disabled={isEditable || !canEditInsurerAdjudications}
              onChange={({ insurerAmount }) => {
                onUpdate({
                  insurerAdjudications: {
                    ...insurerAdjudications,
                    [insurerKey]: {
                      ...value,
                      amount: insurerAmount,
                      currency: value?.currency ?? data?.currency,
                    },
                  } as Model.IInsurerAdjudication,
                });
              }}
              style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
              iconBefore={<Icon class="fas-dollar-sign" />}
            />
          </td>
        );
      })}
      <td style={{ width: '125px' }}>
        <NumberInput
          name="gstHst"
          value={Number(((data?.gstHst || 0.0) * data.adjudication).toFixed(2))}
          onChange={({ gstHst }) =>
            onUpdate({
              gstHst: gstHst / data.adjudication,
            })
          }
          onClick={() => {
            if (isEditable) return;
            setIsEditingTax(true);
          }}
          step={0.01}
          readOnly={!isEditingTax || isEditable}
          htmlElementProps={{ title: !isEditingTax && !isEditable ? lang.clickToEdit : undefined }}
          error={
            data?.gstHst > 1 &&
            lang.mustBeEqualOrLessThanAmount.replace('{amount}', `Adjusted amt: ${data.adjudication.toFixed(2)}`)
          }
          style={{
            input: {
              textAlign: 'right',
              padding: '0 12px 0 0',
              cursor: isEditable ? 'not-allowed' : 'text',
            },
            cursor: isEditable ? 'not-allowed' : 'text',
          }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      {(countryCode === CountryCode.CAN || countryCode === CountryCodeFallback.CA) && (
        <td style={{ width: '125px' }}>
          <NumberInput
            name="provincialSalesTax"
            value={Number(((data?.provincialSalesTax || 0.0) * data.adjudication).toFixed(2))}
            readOnly={!isEditingTax || isEditable}
            htmlElementProps={{ title: !isEditingTax && !isEditable ? lang.clickToEdit : undefined }}
            onChange={({ provincialSalesTax }) =>
              onUpdate({
                provincialSalesTax: provincialSalesTax / data.adjudication,
              })
            }
            onClick={() => {
              if (isEditable) return;
              setIsEditingTax(true);
            }}
            error={
              data?.provincialSalesTax > 1 &&
              lang.mustBeEqualOrLessThanAmount.replace('{amount}', `Adjusted amt: ${data.adjudication.toFixed(2)}`)
            }
            step={0.01}
            style={{
              input: {
                textAlign: 'right',
                padding: '0 12px 0 0',
                cursor: isEditable ? 'not-allowed' : 'text',
              },
              cursor: isEditable ? 'not-allowed' : 'text',
            }}
            iconBefore={<Icon class="fas-dollar-sign" />}
          />
        </td>
      )}
      <td style={{ width: '125px' }}>
        <NumberInputTextValue
          name="total"
          value={Number(
            (
              data.adjudication +
              Number(((data?.provincialSalesTax || 0.0) * data.adjudication).toFixed(2)) +
              Number(((data?.gstHst || 0.0) * data.adjudication).toFixed(2))
            ).toFixed(2)
          )}
          readOnly
          onChange={onUpdate}
          style={{ input: { textAlign: 'right', padding: '0 12px 0 0' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      <td style={{ padding: Theme.Size.S }}>
        <Button
          class={ThemeButtonCircle}
          media={<Icon class="fas-trash" />}
          onClick={(data) => onDelete(data, index)}
          data={data}
          htmlElementProps={{
            title: lang.delete,
          }}
          disabled={isClaimJobItemEditable || !(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMJOBITEM))}
        />
      </td>
      {canAddNewPayee && (
        <ClaimEditJobItemNewPayee
          onCancel={() => setIsCreatingPayee(false)}
          onSubmit={async (payeeData) => {
            const res = await payeeSaveApi(payeeData);
            if (res.status === HttpStatus.ok) {
              addLog({ success: lang.saveSuccess });
              setNewPayeeId(res?.payload);
              setIsCreatingPayee(false);
            } else {
              addLog({ error: res.message });
              setIsCreatingPayee(false);
            }
          }}
        />
      )}
    </tr>
  );
}
